import API_URL from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('armita_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// MRI Image cache service for sagittal slice data
class MRIImageCacheService {
  constructor() {
    this.API_URL = API_URL;
    this.cache = new Map();
    this.centroidsCache = new Map();
    this.volumeInfo = null;
    this.pendingRequests = new Map();
    this.requestQueue = [];
    this.activeRequests = 0;
    this.maxConcurrentRequests = 4; // Higher for MRI since fewer total slices
    this.avgPositions = null;
    this.auxiliaryData = null;
  }

  // Get/Set auxiliary data (crosshairs, etc)
  setAuxiliaryData(data) {
    this.auxiliaryData = data;
  }

  getAuxiliaryData() {
    return this.auxiliaryData;
  }

  // Load volume info
  async loadVolumeInfo() {
    try {
      const response = await fetch(`${this.API_URL}/mri/info`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to load MRI info');
      }
      this.volumeInfo = await response.json();

      // Load average positions too
      const avgResponse = await fetch(`${this.API_URL}/mri/sagittal/avg-positions`, { headers: getAuthHeaders() });
      if (avgResponse.ok) {
        const avgData = await avgResponse.json();
        this.avgPositions = avgData.avg_y_positions;
      }

      return this.volumeInfo;
    } catch (err) {
      console.error('Error loading MRI volume info:', err);
      throw err;
    }
  }

  // Process the request queue
  async processQueue() {
    while (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const request = this.requestQueue.shift();
      this.activeRequests++;

      request().finally(() => {
        this.activeRequests--;
        this.processQueue();
      });
    }
  }

  // Fetch a single slice with caching
  async fetchSlice(sliceIndex, withSegmentation = true) {
    const cacheKey = `sag_${sliceIndex}_${withSegmentation}`;

    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create the fetch promise
    const fetchPromise = new Promise((resolve, reject) => {
      const doFetch = async () => {
        try {
          const response = await fetch(
            `${this.API_URL}/mri/sagittal/slice?slice_index=${sliceIndex}&with_segmentation=${withSegmentation}`,
            { timeout: 10000, headers: getAuthHeaders() }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch slice: ${response.statusText}`);
          }

          const data = await response.json();

          // Create image URL from base64
          const imageUrl = `data:image/png;base64,${data.image_data}`;

          // Cache the processed result
          const cacheData = {
            imageUrl,
            dimensions: data.dimensions,
            centroids: data.centroids || {},
            totalSlices: data.total_slices
          };

          this.cache.set(cacheKey, cacheData);

          // Store centroids separately for quick access
          if (data.centroids) {
            this.centroidsCache.set(sliceIndex, data.centroids);
          }

          // Manage cache size (MRI typically has ~200-300 slices)
          if (this.cache.size > 400) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
          }

          this.pendingRequests.delete(cacheKey);
          resolve(cacheData);
        } catch (err) {
          console.error(`Error fetching MRI slice ${sliceIndex}:`, err);
          this.pendingRequests.delete(cacheKey);
          resolve(null);
        }
      };

      this.requestQueue.push(doFetch);
    });

    this.pendingRequests.set(cacheKey, fetchPromise);
    this.processQueue();

    return fetchPromise;
  }

  // Preload slices around current position
  async preloadSlices(currentSlice, totalSlices, radius = 20) {
    const promises = [];

    // Prioritize loading: current slice first, then adjacent ones
    const loadOrder = [currentSlice];

    // Add adjacent slices in expanding order
    for (let offset = 1; offset <= radius; offset++) {
      if (currentSlice - offset >= 0) loadOrder.push(currentSlice - offset);
      if (currentSlice + offset < totalSlices) loadOrder.push(currentSlice + offset);
    }

    for (const sliceIndex of loadOrder) {
      const cacheKey = `sag_${sliceIndex}_true`;

      // Only preload if not already cached
      if (!this.cache.has(cacheKey) && !this.pendingRequests.has(cacheKey)) {
        promises.push(this.fetchSlice(sliceIndex, true).catch(err => {
          console.warn(`Failed to preload MRI slice ${sliceIndex}:`, err);
        }));
      }
    }

    // Wait for at least the current and adjacent slices
    if (promises.length > 0) {
      // Wait for first few slices before returning
      const criticalSlices = promises.slice(0, 5);
      await Promise.all(criticalSlices);

      // Load the rest in background
      Promise.all(promises.slice(5)).catch(() => { });
    }
  }

  // Preload all slices for maximum performance
  async preloadAllSlices(totalSlices, onProgress) {
    console.log(`Starting to preload all ${totalSlices} MRI slices...`);

    const chunkSize = 10;
    let loaded = 0;

    // Count already cached slices
    for (let i = 0; i < totalSlices; i++) {
      if (this.cache.has(`sag_${i}_true`)) {
        loaded++;
      }
    }

    if (loaded === totalSlices) {
      console.log('All MRI slices already cached');
      if (onProgress) onProgress(100);
      return;
    }

    // Update initial progress
    if (loaded > 0 && onProgress) {
      onProgress((loaded / totalSlices) * 100);
    }

    // Load in chunks
    for (let i = 0; i < totalSlices; i += chunkSize) {
      const promises = [];

      for (let j = i; j < Math.min(i + chunkSize, totalSlices); j++) {
        const cacheKey = `sag_${j}_true`;
        if (!this.cache.has(cacheKey)) {
          promises.push(this.fetchSlice(j, true));
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        loaded += promises.length;

        if (onProgress) {
          onProgress((loaded / totalSlices) * 100);
        }

        // Small delay to prevent UI freeze
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }

    console.log(`Finished preloading all ${totalSlices} MRI slices`);
  }

  // Get slice from cache (returns null if not cached)
  getSlice(sliceIndex, withSegmentation = true) {
    const cacheKey = `sag_${sliceIndex}_${withSegmentation}`;
    return this.cache.get(cacheKey) || null;
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.centroidsCache.clear();
    this.volumeInfo = null;
    this.avgPositions = null;
  }
}

// Create singleton instance
const mriImageCache = new MRIImageCacheService();

export default mriImageCache;